const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Truncates an ISO date or date-time string to its "YYYY-MM-DD" calendar date and
// re-parses that as UTC midnight, discarding both the time-of-day and the caller's
// local timezone. Using date-fns' `differenceInCalendarDays` directly was tried first
// and found wrong here: it compares calendar days in the *local* (test-runner/browser)
// timezone, so a UTC wire timestamp like `dateToApproved` (backend: `OffsetDateTime`,
// UTC on the wire per BayHireRequest.java) can land on a different local calendar day
// depending on where the code runs - caught by a failing test asserting the boundary is
// timezone-independent.
const toUtcMidnight = (value: string): number =>
  new Date(`${value.slice(0, 10)}T00:00:00Z`).getTime();

/**
 * DFT-0016: the single shared definition of "how many days until an approved
 * hire expires" - consumed by both the Renew button's visibility
 * (`canRenewBayHireRequest`) and, per `DFE-BAY-HIRE-004`, the separate
 * `DFT-0019` expiry-reminder task. Kept here as its own pure, dependency-free
 * function (no request/component coupling) so a future reminder job can
 * import the exact same calculation rather than re-deriving it - see
 * `working/05-research.md` Conflicts and Gaps for why that duplication risk
 * matters.
 *
 * Treats both operands as pure calendar dates, matching the epic's own framing
 * ("visible on expiry day", "hidden when > 30 days before expiry") and how
 * `BayInformationCard` already renders `dateToApproved` as a date, not a date-time
 * (`formatBayHireDate`, not `formatBayHireDateTime`) - a request expiring later today
 * is still "0 days until expiry", deterministically, regardless of the current time of
 * day or the caller's local timezone.
 *
 * @param dateToApproved - the approved hire's expiry date (`BayHireRequest.dateToApproved`).
 * @param now - injectable for deterministic tests; defaults to the real current time.
 */
export const daysUntilExpiry = (dateToApproved: string, now: Date = new Date()): number =>
  Math.round((toUtcMidnight(dateToApproved) - toUtcMidnight(now.toISOString())) / MS_PER_DAY);
